import type { RuntimeBrand } from "@webuild-ai/react-ui/types";
import type { Capabilities } from "@/hooks/useCapabilities";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import { useCapabilities } from "@/hooks/useCapabilities";
import { getDashboardConfig } from "@/config/dashboards";
import { brandIconSrc, isFolder } from "@/config/tenants";

import { type SidebarItem } from "./Sidebar";

/**
 * Pure function that builds menu items from capabilities and brand config.
 * Extracted so it can be tested without renderHook (Vitest 4.x jsdom constraint).
 */
export const buildSecureMenuItems = (
  capabilities: Capabilities,
  brand: RuntimeBrand,
  t: (key: string) => string,
): SidebarItem[] => {
  const overviewItems: SidebarItem[] = [];
  const dashboard = getDashboardConfig(brand.folder);

  // Add dashboard as first item if configured to show in sidebar
  if (dashboard?.showInSideBar && dashboard?.url) {
    overviewItems.push({
      key: "dashboard",
      href: `/${dashboard.urlSegment ?? "dashboard"}`,
      icon: "solar:chart-2-outline",
      img: brandIconSrc(brand.folder, "dashboard.svg"),
      title: dashboard.title ? t(dashboard.title) : t("Dashboard"),
    });
  }

  // Chat is available to every tenant.
  overviewItems.push({
    key: "chat",
    href: "/chat",
    icon: "solar:chat-line-linear",
    img: brandIconSrc(brand.folder, "pathfinder.svg"),
    title: t(brand.copy.menuItems?.Pathfinder || "Chat"),
  });

  // Library consolidates the old Workflows and Prompt Library entries and is
  // available to every tenant; per-source permissions are applied inside the page.
  overviewItems.push({
    key: "library",
    href: "/library",
    icon: "solar:notebook-bookmark-outline",
    img: brandIconSrc(brand.folder, "library.svg"),
    title: t(brand.copy.menuItems?.["Library"] || "Library"),
  });

  // Workflow Builder reuses the Playground component set (LSEG-91) and — unlike
  // Playground below — is available to every tenant, including NGV and NGET. It
  // takes the authoring capability, not the `workflows` read flag: the Builder
  // saves, so a read-only role must not reach it.
  //
  // Automated Workflows (LSEG-107) sits directly after the Builder, since a
  // workflow is published in the Builder and then scheduled here. It shares the
  // Builder's gate, because scheduling a workflow and triggering a run are both
  // mutations rather than reads.
  if (capabilities.canAuthorWorkflows) {
    overviewItems.push({
      key: "workflow-builder",
      href: "/workflow-builder",
      icon: "solar:widget-2-linear",
      img: brandIconSrc(brand.folder, "workflow-builder.svg"),
      title: t("workflowBuilder.nav.title"),
    });
    overviewItems.push({
      key: "automations",
      href: "/automations",
      icon: "solar:clock-circle-linear",
      img: brandIconSrc(brand.folder, "workflow-automation.svg"),
      title: t("automations.nav.title"),
    });
  }

  // Check server-validated permissions for each feature. Playground remains
  // restricted to the tenants it originally shipped for, now that Workflow
  // Builder covers NGV and NGET via the entry above.
  if (capabilities.canViewWorkflows && !isFolder(brand, "ngv") && !isFolder(brand, "nget")) {
    overviewItems.push({
      key: "playground",
      href: "/workflows/playground",
      icon: "solar:layers-outline",
      img: brandIconSrc(brand.folder, "playground.svg"),
      title: t("Playground"),
    });
  }

  if (capabilities.canViewKnowledgeHub) {
    overviewItems.push({
      key: "data",
      href: "/datahub",
      icon: "solar:database-linear",
      img: brandIconSrc(brand.folder, "knowledge.svg"),
      title: t(brand.copy.menuItems?.["Knowledge Hub"] || "Knowledge Hub"),
    });
  }

  const settingsItems: SidebarItem[] = [];

  if (capabilities.canViewMonitoring) {
    settingsItems.push({
      key: "monitoring",
      href: "/metrics",
      title: t("Monitoring"),
      icon: "solar:pie-chart-2-outline",
      img: brandIconSrc(brand.folder, "solutions.svg"),
    });

    // Compliance dashboard depends on ChatGov (AWS-only). Hide the entry
    // for non-webuild tenants so Azure-only deployments don't see a broken page.
    if (isFolder(brand, "webuild")) {
      settingsItems.push({
        key: "compliance",
        href: "/compliance",
        title: t("Compliance Check"),
        icon: "solar:eye-scan-outline",
        img: "/brands/webuild/compliance.svg",
      });
    }
  }

  // Admin page is accessed via the profile dropdown menu, not the sidebar

  const items: SidebarItem[] = [];

  if (overviewItems.length > 0) {
    items.push({
      key: "overview",
      title: t("Overview"),
      items: overviewItems,
    });
  }

  if (settingsItems.length > 0) {
    items.push({
      key: "settings",
      title: t("Settings"),
      items: settingsItems,
    });
  }

  return items;
};

/**
 * Sidebar menu items hook, driven by the shared capability layer so the sidebar
 * cannot disagree with the landing tiles or the route guards about what a user
 * may reach.
 *
 * `isValidating` reports whether the RBAC grants have settled. The
 * `SecurePermissions` flags are already resolved by the time any route renders,
 * because `PrivateRoute` holds the app on the loading page until the
 * `verify-permissions` call returns.
 */
export const useSidebarMenuItems = (): {
  menuItems: SidebarItem[];
  isValidating: boolean;
} => {
  const capabilities = useCapabilities();
  const { brand } = useBrand();
  const { t } = useTranslation();
  const [menuItems, setMenuItems] = useState<SidebarItem[]>([]);

  useEffect(() => {
    setMenuItems(buildSecureMenuItems(capabilities, brand, t));
  }, [capabilities, brand, t]);

  return {
    menuItems,
    isValidating: !capabilities.isSettled,
  };
};
